"use client";

import { Avatar, AvatarFallback, AvatarImage } from "@bcc/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@bcc/components/ui/dropdown-menu";
import { Vendor } from "@client-shared/providers";
import { LogOut, Settings, User } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "./ui";
import { setUserNavTestId } from "./user-nav.utils";

interface UserNavProps {
  testId?: string;
}

export function UserNav({ testId }: UserNavProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [vendorState] = Vendor.useVendorContext();
  const { currentVendor } = vendorState;

  const {
    firstName,
    lastName,
    email,
    vendorName: currentVendorName,
  } = currentVendor ?? {};

  const fallBackProfileName = useMemo(
    () =>
      firstName?.charAt(0).toUpperCase() +
      " " +
      lastName?.charAt(0).toUpperCase(),
    [firstName, lastName],
  );

  const vendorName = useMemo(
    () => currentVendorName ?? firstName + " " + lastName,
    [currentVendorName, firstName, lastName],
  );

  if (!currentVendor) {
    return null;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger>
        <Button
          testId={testId ?? setUserNavTestId("trigger")}
          className="relative h-9 w-9 rounded-full cursor-pointer outline-none"
          aria-label={t("userNav.userMenu")}
        >
          <Avatar className="h-9 w-9">
            <AvatarImage src="" alt={vendorName} />
            <AvatarFallback className="bg-primary text-primary-foreground">
              {fallBackProfileName}
            </AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        testId={setUserNavTestId("content")}
        className="w-56"
        align="end"
        forceMount
      >
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col space-y-1">
            <p className="text-sm font-medium leading-none">{vendorName}</p>
            <p className="text-xs leading-none text-muted-foreground">
              {email}
            </p>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          data-testid={setUserNavTestId("profile-item")}
          onClick={() => navigate("/preferences")}
        >
          <User className="mr-2 h-4 w-4" />
          <span>{t("userNav.profile")}</span>
        </DropdownMenuItem>
        <DropdownMenuItem
          data-testid={setUserNavTestId("settings-item")}
          onClick={() => navigate("/preferences")}
        >
          <Settings className="mr-2 h-4 w-4" />
          <span>{t("userNav.settings")}</span>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          data-testid={setUserNavTestId("logout-item")}
          className="text-destructive"
        >
          <LogOut className="mr-2 h-4 w-4" />
          <Link to="/oauth/logout">{t("userNav.logOut")}</Link>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
